import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp, faCircle, faPause, faPlay } from '@fortawesome/free-solid-svg-icons';
import branding from '../../../assets/img/branding.png';
import branding1 from '../../../assets/img/branding1.PNG';
import branding2 from '../../../assets/img/branding2.PNG';
import branding3 from '../../../assets/img/branding3.PNG';

const images = [branding, branding1, branding2, branding3];

const BrandingPage = () => {
  const [currentImage, setCurrentImage] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const intervalRef = useRef(null);
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const startRolling = () => {
    if (!intervalRef.current) {
      setIsPlaying(true);
      intervalRef.current = setInterval(() => {
        setCurrentImage((prev) => (prev + 1) % images.length);
      }, 3000); // Slower interval for better UX
    }
  };

  const stopRolling = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
    setIsPlaying(false);
  };

  const toggleRolling = () => {
    if (isPlaying) {
      stopRolling();
    } else {
      startRolling();
    }
  };

  const goToNext = () => {
    setCurrentImage((prev) => (prev + 1) % images.length);
    if (isPlaying) {
      stopRolling();
      startRolling(); // Reset timer
    }
  };

  const goToPrev = () => {
    setCurrentImage((prev) => (prev - 1 + images.length) % images.length);
    if (isPlaying) {
      stopRolling();
      startRolling(); // Reset timer
    }
  };

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const difference = touchStartX.current - touchEndX.current;
    if (difference > 50) { // Swipe left
      goToNext();
    } else if (difference < -50) { // Swipe right
      goToPrev();
    }
    
    touchStartX.current = null;
    touchEndX.current = null;
  };

  useEffect(() => {
    startRolling(); // Auto-play on load
    return () => stopRolling(); // Cleanup on unmount
  }, []);

  return (
    <div className='p-4 md:p-8 max-w-7xl mx-auto'>
      {/* Header Section */}
      <div className='flex flex-col md:flex-row items-center justify-between p-4 md:p-6  rounded-xl  mb-6'>
        <h2 className='text-2xl md:text-4xl font-bold text-gray-800 mb-4 md:mb-0'>
          Our <span className='text-[#FD5F20]'>Branding</span> Work
        </h2>
        <button 
          onClick={toggleRolling}
          className='flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#FD5F20] to-[#FF8C00] text-white rounded-lg  hover: transition-all'
        >
          <span className='text-lg md:text-xl font-medium'>
            {isPlaying ? 'Pause' : 'View Gallery'}
          </span>
          <FontAwesomeIcon 
            icon={isPlaying ? faPause : faPlay} 
            className='text-white'
          />
        </button>
      </div>

      {/* Image Carousel */}
      <div className='relative group'>
        <div 
          className='relative w-full overflow-hidden rounded-2xl '
          onMouseEnter={stopRolling}
          onMouseLeave={() => isPlaying && startRolling()}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <img 
            src={images[currentImage]} 
            alt={`Branding example ${currentImage + 1}`} 
            className='w-full h-auto md:h-[70vh] object-cover transition-opacity duration-500'
          />
          
          {/* Navigation Arrows */}
          <button 
            onClick={goToPrev}
            className='absolute left-4 top-1/2 -translate-y-1/2 bg-black/30 text-white p-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity md:block hidden'
          >
            <FontAwesomeIcon icon={faArrowUp} className='transform -rotate-90 text-xl' />
          </button>
          <button 
            onClick={goToNext}
            className='absolute right-4 top-1/2 -translate-y-1/2 bg-black/30 text-white p-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity md:block hidden'
          >
            <FontAwesomeIcon icon={faArrowUp} className='transform rotate-90 text-xl' />
          </button>
        </div>

        {/* Indicators */}
        <div className='flex justify-center mt-4 space-x-2'>
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setCurrentImage(index);
                if (isPlaying) {
                  stopRolling();
                  startRolling();
                }
              }}
              className={`w-3 h-3 rounded-full transition-all ${currentImage === index ? 'bg-[#FD5F20] w-6' : 'bg-gray-300'}`}
            />
          ))}
        </div>

        {/* Mobile Navigation */}
        <div className='flex justify-between mt-4 md:hidden'>
          <button 
            onClick={goToPrev}
            className='bg-[#FD5F20] text-white p-3 rounded-full '
          >
            <FontAwesomeIcon icon={faArrowUp} className='transform -rotate-90' />
          </button>
          <button 
            onClick={toggleRolling}
            className='bg-[#FD5F20] text-white p-3 rounded-full '
          >
            <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
          </button>
          <button 
            onClick={goToNext}
            className='bg-[#FD5F20] text-white p-3 rounded-full '
          >
            <FontAwesomeIcon icon={faArrowUp} className='transform rotate-90' />
          </button>
        </div>
      </div>

      {/* Call to Action */}
      <div className='mt-8 text-center'>
        <h3 className='text-xl md:text-2xl font-semibold text-gray-800 mb-4'>
          Ready to elevate your brand?
        </h3>
        <button className='px-8 py-4 bg-gradient-to-r from-[#FD5F20] to-[#FF8C00] text-white text-lg font-medium rounded-xl  hover: transition-all transform hover:scale-105'>
          Start Your Branding Journey Today
          <FontAwesomeIcon icon={faArrowUp} className='transform rotate-45 ml-2' />
        </button>
      </div>
    </div>
  );
};

export default BrandingPage;